import { clinicsFetch } from '../functions/fetchClinics.js'

const getClinic = (s) => {
  const id = new URLSearchParams(window.location.search).get('id')
  return (s.root.clinics || []).find(x => x.id === id)
}

export const clinic = {
  extends: 'Page',
  flow: 'y',
  background: 'ink',
  color: 'inkText',

  ClinicsData: { fetch: clinicsFetch, display: 'none' },

  NavBar: {},

  ClinicDetail: {
    flow: 'y',
    gap: 'A',
    padding: 'C B B B',
    width: '100%',
    flex: '1',
    '@mobileL': { padding: 'B A A A' },

    BackRow: {
      extends: 'Link',
      href: '/clinics',
      text: '{{ backToAllClinics | polyglot }}',
      fontSize: 'Y',
      color: 'inkMuted',
      textDecoration: 'none',
      fontWeight: '500',
      marginBottom: 'Y',
      ':hover': { color: 'inkText' }
    },

    MainLayout: {
      flow: 'x',
      gap: 'A',
      width: '100%',
      align: 'stretch flex-start',
      '@mobileL': { flow: 'y' },

      LeftCol: {
        flex: '1',
        flow: 'y',
        gap: 'A',
        minWidth: '0',

        HeaderCard: {
          flow: 'x',
          gap: 'A',
          padding: 'B',
          round: 'A',
          background: 'inkMid',
          align: 'flex-start flex-start',
          '@mobileL': { flow: 'y' },

          ClinicLogo: {
            tag: 'img',
            width: 'E',
            height: 'E',
            round: 'Z',
            flexShrink: '0',
            attr: {
              src: (el, s) => {
                const c = getClinic(s)
                return c ? (c.logo || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(c.name) + '&background=0D1117&color=00C9A7&size=128&bold=true&format=svg') : ''
              }
            }
          },

          ClinicInfo: {
            flow: 'y',
            gap: 'Y',
            flex: '1',

            NameRow: {
              flow: 'x',
              align: 'center space-between',
              ClinicName: {
                tag: 'h1',
                text: (el, s) => { const c = getClinic(s); return c ? c.name : 'Clinic not found' },
                fontSize: 'C',
                fontWeight: '700',
                letterSpacing: '-0.02em'
              },
              RatingBadge: {
                flow: 'x',
                gap: 'W',
                align: 'center center',
                padding: 'Y Z',
                round: 'Y',
                background: 'teal.1',
                flexShrink: '0',
                RatingStar: { extends: 'Icon', icon: 'star', width: 'Z', height: 'Z', color: 'teal' },
                RatingVal: { text: (el, s) => { const c = getClinic(s); return c ? String(c.rating) : '' }, fontSize: 'Z', fontWeight: '600', color: 'teal' },
                ReviewCount: { text: (el, s) => { const c = getClinic(s); return c ? '(' + c.reviews + ')' : '' }, fontSize: 'Y', color: 'inkMuted' }
              }
            },

            AddressText: {
              flow: 'x', gap: 'Y', align: 'center flex-start',
              PinIcon: { extends: 'Icon', icon: 'mapPin', width: 'Z', height: 'Z', color: 'inkMuted' },
              Addr: { text: (el, s) => { const c = getClinic(s); return c ? c.address + ', ' + c.district + ', Tbilisi' : '' }, fontSize: 'Z', color: 'inkMuted' }
            },
            ContactRow: {
              flow: 'x', gap: 'A', align: 'center flex-start',
              PhoneLink: {
                extends: 'Link',
                href: (el, s) => { const c = getClinic(s); return c ? 'tel:' + c.phone.replace(/\s/g, '') : '#' },
                textDecoration: 'none',
                flow: 'x', gap: 'Y', align: 'center flex-start',
                PhoneIcon: { extends: 'Icon', icon: 'phone', width: 'Z', height: 'Z', color: 'inkMuted' },
                PhoneText: { text: (el, s) => { const c = getClinic(s); return c ? c.phone : '' }, fontSize: 'Z', color: 'teal', fontWeight: '500' }
              },
              HoursInfo: {
                flow: 'x', gap: 'Y', align: 'center flex-start',
                ClockIcon: { extends: 'Icon', icon: 'clock', width: 'Z', height: 'Z', color: 'inkMuted' },
                HoursText: { text: (el, s) => { const c = getClinic(s); return c ? c.hours : '' }, fontSize: 'Z', color: 'inkMuted' }
              }
            },

            ServiceTags: {
              flow: 'x',
              gap: 'Y',
              flexWrap: 'wrap',
              marginTop: 'Y',
              children: (el, s) => { const c = getClinic(s); return c ? c.services.map(sv => ({ label: sv })) : [] },
              childrenAs: 'state',
              childProps: {
                text: (el, s) => s.label,
                padding: 'W Y',
                round: 'C',
                fontSize: 'Y',
                fontWeight: '500',
                background: 'inkSurface',
                color: 'inkSubtle'
              }
            },

            PriceRow: {
              flow: 'x', gap: 'Y', align: 'center flex-start', marginTop: 'Y',
              PriceLabel: { text: '{{ priceRange2 | polyglot }}', fontSize: 'Z', color: 'inkMuted' },
              PriceValue: { text: (el, s) => { const c = getClinic(s); return c ? c.priceRange : '' }, fontSize: 'Z', fontWeight: '600', color: 'inkText' }
            }
          }
        },

        MapCard: {
          round: 'A',
          overflow: 'hidden',
          border: '1px solid inkBorder',
          MapContainer: {
            width: '100%',
            flex: '1',
            minHeight: '350px',
            attr: { id: 'clinic-detail-map' },
            onRender: (el, s) => {
              if (el.node._mapInit) return
              el.node._mapInit = true
              const c = getClinic(s)
              if (!c || !c.lat || !c.lng) return

              const initMap = () => {
                const mapStyles = [
                  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e3e8ee' }] },
                  { featureType: 'landscape', stylers: [{ color: '#f5f5f5' }] },
                  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] }
                ]
                const map = new google.maps.Map(el.node, {
                  center: { lat: c.lat, lng: c.lng },
                  zoom: 15,
                  styles: mapStyles,
                  disableDefaultUI: true,
                  zoomControl: true
                })
                new google.maps.Marker({
                  position: { lat: c.lat, lng: c.lng },
                  map,
                  title: c.name,
                  icon: {
                    path: 'M12 2C9.5 2 7 3.5 6 5.5c-1.5 3-.5 5 0 7.5.5 2.5-.5 5.5 1 8 .5.8 1.5 1 2 1s1-.5 1.5-2c.5-1.5 1-2 1.5-2s1 .5 1.5 2c.5 1.5 1 2 1.5 2s1.5-.2 2-1c1.5-2.5.5-5.5 1-8 .5-2.5 1.5-4.5 0-7.5C17 3.5 14.5 2 12 2z',
                    fillColor: '#0D1117', fillOpacity: 1, strokeColor: '#FFFFFF', strokeWeight: 2, scale: 1.5,
                    anchor: new google.maps.Point(12, 22)
                  }
                })
              }
              if (window.google && window.google.maps) initMap()
              else {
                window.initClinicDetailMap = initMap
                const script = document.createElement('script')
                script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyAIjI_gvh-OUSEdzgfHKh2v1GvwiEp2Ztg&callback=initClinicDetailMap'
                script.async = true
                script.defer = true
                document.head.appendChild(script)
              }
            }
          }
        }
      },

      RightCol: {
        width: '380px',
        flexShrink: '0',
        flow: 'y',
        gap: 'A',
        '@mobileL': { width: '100%' },

        BookingCard: {
          flow: 'y',
          gap: 'A',
          padding: 'A',
          round: 'A',
          background: 'inkMid',
          border: '1px solid inkBorder',

          BookTitle: { text: '{{ bookAnAppointment | polyglot }}', fontSize: 'A', fontWeight: '700', color: 'inkText' },

          DatePicker: {
            flow: 'y',
            gap: 'Y',
            DateLabel: { text: '{{ selectDate2 | polyglot }}', fontSize: 'Y', fontWeight: '600', color: 'inkMuted', textTransform: 'uppercase', letterSpacing: '0.05em' },
            DateGrid: {
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 'W',
              children: (el, s) => s.root.dates,
              childrenAs: 'state',
              childExtends: 'DateOption'
            }
          },

          TimePicker: {
            flow: 'y',
            gap: 'Y',
            TimeLabel: { text: '{{ selectTime2 | polyglot }}', fontSize: 'Y', fontWeight: '600', color: 'inkMuted', textTransform: 'uppercase', letterSpacing: '0.05em' },
            TimeGrid: {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'W',
              children: (el, s) => s.root.times,
              childrenAs: 'state',
              childExtends: 'TimeOption'
            }
          },

          BookBtn: {
            extends: 'Link',
            href: '/booking',
            text: '{{ confirmBooking2 | polyglot }}',
            background: '#0D1117',
            color: '#FFFFFF',
            fontWeight: '600',
            padding: 'Z1 C',
            round: 'Z',
            fontSize: 'Z',
            textDecoration: 'none',
            textAlign: 'center',
            width: '100%',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': { opacity: '0.9' },
            attr: { style: 'display:block; text-align:center' }
          }
        }
      }
    }
  },

  FooterSection: {
    tag: 'footer',
    flow: 'y',
    gap: 'Z',
    padding: 'B B',
    width: '100%',
    borderTop: '1px solid inkBorder',
    align: 'center center',
    FooterTop: {
      flow: 'x',
      align: 'center space-between',
      width: '100%',
      maxWidth: 'K',
      '@mobileL': { flow: 'y', gap: 'A', align: 'center center' },
      FooterLogo: { text: 'MCAI', fontSize: 'A', fontWeight: '700', color: 'inkText' },
      FooterLinks: {
        flow: 'x', gap: 'B',
        ClinicsLink: { extends: 'Link', text: '{{ clinics | polyglot }}', href: '/clinics', color: 'inkMuted', fontSize: 'Z', textDecoration: 'none', fontWeight: '500', ':hover': { color: 'inkText' }, transition: 'color 0.2s ease' },
        BusinessLink: { extends: 'Link', text: '{{ forBusiness2 | polyglot }}', href: '/business', color: 'inkMuted', fontSize: 'Z', textDecoration: 'none', fontWeight: '500', ':hover': { color: 'inkText' }, transition: 'color 0.2s ease' }
      },
      FooterContact: { text: 'hello@mcai.ge', fontSize: 'Z', color: 'inkMuted' }
    },
    FooterCopy: { text: '{{ u00a92026Mcai | polyglot }}', fontSize: 'Y', color: 'inkMuted', marginTop: 'Y' }
  }
}
